/**
 * Carrier MCP — Pricing & Credits MCP Tools
 *
 * Exposes credit management, billing configuration, and usage analytics
 * as MCP tools. These tools allow AI agents and users to:
 *   - Check credit balance and usage
 *   - Configure billing thresholds
 *   - View volume discount status
 *   - Manage overage settings
 *   - View billing history and projections
 *
 * Inspired by Stripe Projects CLI patterns:
 *   - `stripe projects billing show` → credit_balance
 *   - `stripe projects upgrade` → upgrade_plan
 *   - `stripe projects billing add` → configure_billing
 */

import type { Env, CarrierProps, ToolScope } from "./types.js";
import {
  checkCredits,
  getCreditSummary,
  updateCreditConfig,
  TIER_CREDIT_ALLOTMENTS,
  SCOPE_CREDIT_COSTS,
  VOLUME_DISCOUNTS,
  DAILY_FREE_CREDITS,
  type CreditConfig,
} from "./credits.js";
import {
  getThresholdConfig,
  updateThresholdConfig,
  type ThresholdConfig,
} from "./billing-thresholds.js";
import type { Tier } from "./billing.js";
import { requireUserStore } from "./binding-unavailable.js";

// ---------------------------------------------------------------------------
// Tool Definitions (for registration in tools.ts)
// ---------------------------------------------------------------------------

export interface PricingToolDef {
  name: string;
  description: string;
  scope: ToolScope;
  inputSchema: Record<string, unknown>;
  handler: (
    env: Env,
    props: CarrierProps,
    args: Record<string, unknown>,
  ) => Promise<unknown>;
}

export const PRICING_TOOLS: PricingToolDef[] = [
  // -------------------------------------------------------------------------
  // credit_balance — View current credit balance and usage
  // -------------------------------------------------------------------------
  {
    name: "credit_balance",
    description:
      "View your current credit balance, daily free credits, overage status, " +
      "volume discount tier, and billing threshold status. Use this to understand " +
      "your current usage and remaining capacity before making API calls.",
    scope: "read",
    inputSchema: {
      type: "object",
      properties: {},
      required: [],
    },
    handler: async (env, props) => {
      requireUserStore(env, "credit_balance", "your MCP plan credit ledger");
      const tier = props.tier as Tier;
      const summary = await getCreditSummary(env, props.sub, tier);
      const creditCheck = await checkCredits(env, props.sub, tier, "read");

      return {
        tier,
        credits: {
          monthly_allotment: summary.ledger.allotment,
          consumed: summary.ledger.consumed,
          remaining: Math.max(0, summary.ledger.allotment - summary.ledger.consumed),
          rollover_applied: summary.ledger.rollover_applied,
          overage_consumed: summary.ledger.overage,
          overage_billed_cents: summary.ledger.overage_billed_cents,
        },
        daily_free: {
          granted: summary.daily_free.granted,
          consumed: summary.daily_free.consumed,
          remaining: Math.max(0, summary.daily_free.granted - summary.daily_free.consumed),
        },
        volume_discount: {
          current_pct: summary.volume_discount_pct,
          next_tier: getNextVolumeDiscountTier(summary.ledger.consumed),
        },
        billing: {
          overages_enabled: summary.config.overages_enabled,
          threshold_triggered: summary.ledger.threshold_triggered,
          billing_threshold_cents: summary.config.billing_threshold_cents,
          overage_rate_cents: summary.config.overage_rate_cents,
        },
        rollover: summary.rollover
          ? {
              credits: summary.rollover.credits,
              source_month: summary.rollover.source_month,
              expires_at: summary.rollover.expires_at,
            }
          : null,
        reset_at: creditCheck.reset_at,
        credit_costs: SCOPE_CREDIT_COSTS,
      };
    },
  },

  // -------------------------------------------------------------------------
  // configure_billing — Update billing preferences
  // -------------------------------------------------------------------------
  {
    name: "configure_billing",
    description:
      "Configure your billing preferences: enable/disable overages, set billing " +
      "thresholds (bill shock prevention), configure notification percentages, " +
      "and set hard spending caps. Pro and Enterprise tiers only.",
    scope: "write",
    inputSchema: {
      type: "object",
      properties: {
        overages_enabled: {
          type: "boolean",
          description: "Enable or disable auto-billed overages when monthly credits are exhausted",
        },
        billing_threshold_cents: {
          type: "number",
          description: "Amount in cents at which an invoice is automatically generated (e.g., 10000 = $100)",
        },
        hard_cap_cents: {
          type: "number",
          description: "Maximum overage spend in cents before overages are paused (0 = no cap)",
        },
        auto_pause_on_cap: {
          type: "boolean",
          description: "Whether to automatically pause overages when hard cap is reached",
        },
        notify_at_pct: {
          type: "array",
          items: { type: "number" },
          description: "Percentage thresholds at which to send notifications (e.g., [50, 80, 95, 100])",
        },
        notification_email: {
          type: "string",
          description: "Email address for billing threshold notifications",
        },
        webhook_url: {
          type: "string",
          description: "Webhook URL for billing threshold events",
        },
      },
      required: [],
    },
    handler: async (env, props, args) => {
      requireUserStore(env, "configure_billing", "your billing configuration");
      const tier = props.tier as Tier;
      if (tier === "free") {
        return {
          error: "Billing configuration requires Pro or Enterprise tier",
          upgrade_url: "https://mcp.carrier.llc/upgrade",
        };
      }

      // Update credit config
      const creditUpdates: Partial<CreditConfig> = {};
      if (typeof args.overages_enabled === "boolean") {
        creditUpdates.overages_enabled = args.overages_enabled;
      }
      if (typeof args.billing_threshold_cents === "number") {
        creditUpdates.billing_threshold_cents = args.billing_threshold_cents;
      }
      if (Array.isArray(args.notify_at_pct)) {
        creditUpdates.notify_at_pct = args.notify_at_pct as number[];
      }

      const updatedCreditConfig = Object.keys(creditUpdates).length > 0
        ? await updateCreditConfig(env, props.sub, creditUpdates)
        : await getCreditConfigForSub(env, props.sub, tier);

      // Update threshold config
      const thresholdUpdates: Partial<ThresholdConfig> = {};
      if (typeof args.hard_cap_cents === "number") {
        thresholdUpdates.hard_cap_cents = args.hard_cap_cents;
      }
      if (typeof args.auto_pause_on_cap === "boolean") {
        thresholdUpdates.auto_pause_on_cap = args.auto_pause_on_cap;
      }
      if (typeof args.notification_email === "string") {
        thresholdUpdates.notification_email = args.notification_email;
      }
      if (typeof args.webhook_url === "string") {
        thresholdUpdates.webhook_url = args.webhook_url;
      }

      const updatedThresholdConfig = Object.keys(thresholdUpdates).length > 0
        ? await updateThresholdConfig(env, props.sub, thresholdUpdates)
        : await getThresholdConfig(env, props.sub, tier);

      return {
        status: "updated",
        credit_config: {
          overages_enabled: updatedCreditConfig.overages_enabled,
          billing_threshold_cents: updatedCreditConfig.billing_threshold_cents,
          overage_rate_cents: updatedCreditConfig.overage_rate_cents,
          notify_at_pct: updatedCreditConfig.notify_at_pct,
        },
        threshold_config: {
          hard_cap_cents: updatedThresholdConfig.hard_cap_cents,
          auto_pause_on_cap: updatedThresholdConfig.auto_pause_on_cap,
          invoice_threshold_cents: updatedThresholdConfig.invoice_threshold_cents,
          notification_email: updatedThresholdConfig.notification_email,
          webhook_url: updatedThresholdConfig.webhook_url,
        },
      };
    },
  },

  // -------------------------------------------------------------------------
  // usage_projection — Project future usage and costs
  // -------------------------------------------------------------------------
  {
    name: "usage_projection",
    description:
      "Project your credit usage and costs for the remainder of the billing period " +
      "based on current consumption rate. Includes overage cost estimates and " +
      "recommendations for plan changes.",
    scope: "read",
    inputSchema: {
      type: "object",
      properties: {
        days_to_project: {
          type: "number",
          description: "Number of days to project forward (default: remaining days in month)",
        },
      },
      required: [],
    },
    handler: async (env, props, args) => {
      requireUserStore(
        env,
        "usage_projection",
        "the consumption history a projection is drawn from",
      );
      const tier = props.tier as Tier;
      const summary = await getCreditSummary(env, props.sub, tier);

      const now = new Date();
      const dayOfMonth = now.getUTCDate();
      const daysInMonth = new Date(
        now.getUTCFullYear(),
        now.getUTCMonth() + 1,
        0,
      ).getUTCDate();
      const daysRemaining = typeof args.days_to_project === "number"
        ? args.days_to_project
        : daysInMonth - dayOfMonth;

      // Calculate daily burn rate
      const dailyBurnRate = dayOfMonth > 0 ? summary.ledger.consumed / dayOfMonth : 0;
      const projectedTotal = Math.round(summary.ledger.consumed + dailyBurnRate * daysRemaining);
      const projectedOverage = Math.max(0, projectedTotal - summary.ledger.allotment);

      // Calculate projected overage cost
      const volumeDiscount = getVolumeDiscountForUsage(projectedTotal);
      const effectiveRate = summary.config.overage_rate_cents * (1 - volumeDiscount / 100);
      const projectedOverageCents = Math.round(projectedOverage * effectiveRate);

      // Recommendation
      const recommendation = generateRecommendation(
        tier,
        projectedTotal,
        summary.ledger.allotment,
        projectedOverageCents,
      );

      return {
        current_period: {
          day_of_month: dayOfMonth,
          days_in_month: daysInMonth,
          days_remaining: daysRemaining,
        },
        usage: {
          consumed_to_date: summary.ledger.consumed,
          daily_burn_rate: Math.round(dailyBurnRate),
          projected_total: projectedTotal,
          allotment: summary.ledger.allotment,
        },
        overage_projection: {
          projected_overage_credits: projectedOverage,
          projected_overage_cents: projectedOverageCents,
          volume_discount_pct: volumeDiscount,
          effective_rate_cents: effectiveRate,
        },
        recommendation,
      };
    },
  },

  // -------------------------------------------------------------------------
  // pricing_plans — View available plans and pricing
  // -------------------------------------------------------------------------
  {
    name: "pricing_plans",
    description:
      "View all available Carrier MCP pricing plans with credit allotments, " +
      "features, overage rates, and volume discount tiers. Use this to compare " +
      "plans and understand upgrade benefits.",
    scope: "read",
    inputSchema: {
      type: "object",
      properties: {},
      required: [],
    },
    handler: async (_env, props) => {
      const currentTier = props.tier as Tier;

      return {
        current_plan: currentTier,
        plans: [
          {
            id: "free",
            name: "Free",
            price_monthly_cents: 0,
            credits_monthly: TIER_CREDIT_ALLOTMENTS.free,
            daily_free_credits: DAILY_FREE_CREDITS,
            scopes: ["read"],
            features: [
              "5,000 monthly credits",
              "50 daily free credits",
              "Read-only OCS access",
              "Basic fleet monitoring",
            ],
            overages: false,
            rollovers: false,
            volume_discounts: false,
          },
          {
            id: "pro",
            name: "Pro",
            price_monthly_cents: 4_900,
            credits_monthly: TIER_CREDIT_ALLOTMENTS.pro,
            daily_free_credits: DAILY_FREE_CREDITS,
            scopes: ["read", "write"],
            features: [
              "100,000 monthly credits",
              "50 daily free credits",
              "Read + Write OCS access",
              "Intelligence composites",
              "Auto-billed overages at $0.001/credit",
              "Credit rollovers (unused → next month)",
              "Volume discounts (up to 25% off overages)",
              "Billing thresholds (bill shock prevention)",
              "Configurable hard spending caps",
              "Priority support",
            ],
            overages: true,
            rollovers: true,
            volume_discounts: true,
            overage_rate_cents: 0.1,
          },
          {
            id: "enterprise",
            name: "Enterprise",
            price_monthly_cents: 49_900,
            credits_monthly: "unlimited",
            daily_free_credits: DAILY_FREE_CREDITS,
            scopes: ["read", "write", "admin"],
            features: [
              "Unlimited credits",
              "All OCS scopes (incl. admin)",
              "Intelligence composites",
              "Custom rate limits",
              "SSO / SAML",
              "Dedicated support",
              "SLA guarantee",
              "Custom billing terms",
            ],
            overages: false,
            rollovers: false,
            volume_discounts: false,
          },
        ],
        volume_discount_tiers: VOLUME_DISCOUNTS,
        credit_costs: SCOPE_CREDIT_COSTS,
        upgrade_url: "https://mcp.carrier.llc/upgrade",
      };
    },
  },

  // -------------------------------------------------------------------------
  // billing_events — View recent billing threshold events
  // -------------------------------------------------------------------------
  {
    name: "billing_events",
    description:
      "View recent billing threshold events: notifications, invoice triggers, " +
      "hard cap alerts, and overage pauses. Useful for monitoring spend and " +
      "understanding billing activity.",
    scope: "read",
    inputSchema: {
      type: "object",
      properties: {
        limit: {
          type: "number",
          description: "Maximum number of events to return (default: 20, max: 50)",
        },
      },
      required: [],
    },
    handler: async (env, props, args) => {
      // Refuses rather than returning `events: []`. An empty list here is
      // indistinguishable from "nothing has happened on your account", which is
      // a different and much more reassuring statement than "this process
      // cannot see your account".
      requireUserStore(env, "billing_events", "your billing event history");
      const limit = Math.min(
        typeof args.limit === "number" ? args.limit : 20,
        50,
      );

      const eventsKey = `threshold:events:${props.sub}`;
      const raw = await env.CARRIER_USERS.get(eventsKey, "json").catch(() => null);
      const events = Array.isArray(raw) ? raw.slice(-limit) : [];

      return {
        events,
        total: events.length,
        threshold_config: await getThresholdConfig(env, props.sub, props.tier as Tier),
      };
    },
  },
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getNextVolumeDiscountTier(
  consumed: number,
): { threshold: number; discount_pct: number; credits_until: number } | null {
  for (const tier of VOLUME_DISCOUNTS) {
    if (consumed < tier.threshold) {
      return {
        threshold: tier.threshold,
        discount_pct: tier.discountPct,
        credits_until: tier.threshold - consumed,
      };
    }
  }
  return null; // Already at max discount
}

function getVolumeDiscountForUsage(usage: number): number {
  let discount = 0;
  for (const tier of VOLUME_DISCOUNTS) {
    if (usage >= tier.threshold) {
      discount = tier.discountPct;
    } else {
      break;
    }
  }
  return discount;
}

function generateRecommendation(
  tier: Tier,
  projectedTotal: number,
  allotment: number,
  projectedOverageCents: number,
): { action: string; reason: string; savings_cents?: number } {
  if (tier === "free" && projectedTotal > allotment * 0.8) {
    return {
      action: "upgrade_to_pro",
      reason:
        "You're projected to exceed 80% of your free tier credits. " +
        "Pro gives you 100,000 credits/month with overages, rollovers, and volume discounts.",
    };
  }

  if (tier === "pro" && projectedOverageCents > 4_900) {
    // Overages exceed the cost of Enterprise
    return {
      action: "upgrade_to_enterprise",
      reason:
        "Your projected overage costs exceed the Enterprise plan price. " +
        "Enterprise gives unlimited credits at $499/month.",
      savings_cents: projectedOverageCents - 4_900 + 4_900, // overage + pro fee vs enterprise
    };
  }

  if (tier === "pro" && projectedTotal < allotment * 0.3) {
    return {
      action: "no_change",
      reason:
        "You're using less than 30% of your Pro allotment. " +
        "Unused credits will roll over to next month (up to 2x your allotment).",
    };
  }

  return {
    action: "no_change",
    reason: "Your current plan is well-suited to your usage pattern.",
  };
}

async function getCreditConfigForSub(
  env: Env,
  sub: string,
  tier: Tier,
): Promise<CreditConfig> {
  const key = `credits:config:${sub}`;
  const raw = await env.CARRIER_USERS.get(key, "json").catch(() => null);
  if (raw && typeof raw === "object" && "billing_threshold_cents" in raw) {
    return raw as CreditConfig;
  }
  return {
    billing_threshold_cents: 10_000,
    overages_enabled: tier !== "free",
    rollovers_enabled: tier !== "free",
    overage_rate_cents: 0.1,
    volume_discount_pct: 0,
    notify_at_pct: [50, 80, 95, 100],
    updated_at: new Date().toISOString(),
  };
}
